import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Bell, BellOff, Volume2, Vibrate, Play, Check, X, Settings } from 'lucide-react';
import {
  NOTIFICATION_SOUNDS,
  getSelectedSound,
  setSelectedSound,
  getSoundEnabled,
  setSoundEnabled,
  getVibrationEnabled,
  setVibrationEnabled,
  testNotificationSound,
} from '../services/notificationSound';

const NotificationSettings = ({ isOpen, onClose, userType = 'user' }) => {
  const [soundEnabled, setSoundEnabledState] = useState(true);
  const [vibrationEnabled, setVibrationEnabledState] = useState(true);
  const [selectedSoundKey, setSelectedSoundKey] = useState('default');
  const [testingSound, setTestingSound] = useState(null);

  useEffect(() => {
    setSoundEnabledState(getSoundEnabled());
    setVibrationEnabledState(getVibrationEnabled());
    setSelectedSoundKey(getSelectedSound());
  }, [isOpen]);

  const handleSoundToggle = () => {
    const newValue = !soundEnabled;
    setSoundEnabledState(newValue);
    setSoundEnabled(newValue);
  };

  const handleVibrationToggle = () => {
    const newValue = !vibrationEnabled;
    setVibrationEnabledState(newValue);
    setVibrationEnabled(newValue);
  };

  const handleSoundSelect = (soundKey) => {
    setSelectedSoundKey(soundKey);
    setSelectedSound(soundKey);
  };

  const handleTestSound = (soundKey) => {
    setTestingSound(soundKey);
    testNotificationSound(soundKey);
    setTimeout(() => setTestingSound(null), 1000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-6 relative animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={20} />
        </button>
        
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
            <Bell className="text-amber-600" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Notification Settings</h2>
            <p className="text-sm text-gray-500">
              {userType === 'driver' ? 'Get alerted for new ride requests' : 'Get alerted for ride updates'}
            </p>
          </div>
        </div>

        {/* Sound Toggle */}
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              {soundEnabled ? (
                <Volume2 className="text-amber-600" size={22} />
              ) : (
                <BellOff className="text-gray-400" size={22} />
              )}
              <div>
                <p className="font-medium">Sound Alerts</p>
                <p className="text-xs text-gray-500">Play sound for notifications</p>
              </div>
            </div>
            <button 
              onClick={handleSoundToggle}
              className={`relative w-14 h-7 rounded-full transition-colors ${soundEnabled ? 'bg-amber-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute w-6 h-6 bg-white rounded-full top-0.5 transition-transform shadow ${soundEnabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Vibration Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Vibrate className={vibrationEnabled ? 'text-amber-600' : 'text-gray-400'} size={22} />
              <div>
                <p className="font-medium">Vibration</p>
                <p className="text-xs text-gray-500">Vibrate on notifications</p>
              </div>
            </div>
            <button 
              onClick={handleVibrationToggle}
              className={`relative w-14 h-7 rounded-full transition-colors ${vibrationEnabled ? 'bg-amber-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute w-6 h-6 bg-white rounded-full top-0.5 transition-transform shadow ${vibrationEnabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Sound Selection */}
          {soundEnabled && (
            <div className="pt-4 border-t">
              <Label className="text-sm font-semibold text-gray-700 mb-3 block">
                Choose Alert Sound
              </Label>
              <div className="space-y-2">
                {Object.entries(NOTIFICATION_SOUNDS).map(([key, sound]) => (
                  <div 
                    key={key}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                      selectedSoundKey === key 
                        ? 'bg-amber-100 border-2 border-amber-500' 
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                    onClick={() => handleSoundSelect(key)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        selectedSoundKey === key ? 'bg-amber-500' : 'bg-gray-200'
                      }`}>
                        {selectedSoundKey === key ? (
                          <Check size={16} className="text-white" />
                        ) : (
                          <Volume2 size={14} className="text-gray-500" />
                        )}
                      </div>
                      <span className={`font-medium ${selectedSoundKey === key ? 'text-amber-800' : 'text-gray-700'}`}>
                        {sound.name}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTestSound(key);
                      }}
                      className={`${testingSound === key ? 'bg-amber-200' : ''}`}
                    >
                      <Play size={14} className={testingSound === key ? 'text-amber-600' : 'text-gray-500'} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info Note */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Tip:</strong> {userType === 'driver' 
                ? 'Keep your sound on to never miss a ride request!' 
                : 'Enable notifications to know when your driver arrives.'}
            </p>
          </div>

          {/* Save Button */}
          <Button 
            onClick={onClose}
            className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 mt-4"
          >
            Save Settings
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default NotificationSettings;
